import { FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';

import {
  BottomSheet,
  Icon,
  Select,
  Option,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { BottomSheetTitle } from 'src/components/BottomSheetTitle/BottomSheetTitle';
import {
  BuildingSelect,
  BuildingSelectItem,
  toBuilding,
  toBuildingSelectItem,
} from 'src/components/BuildingSelect/BuildingSelect';
import {
  FloorSelect,
  FloorSelectItem,
  toFloor,
  toFloorSelectItem,
} from 'src/components/FloorSelect/FloorSelect';
import { RoomAttributes } from 'src/components/RoomAttributes/RoomAttributes';
import { Section } from 'src/components/Section/Section';
import {
  toTown,
  toTownSelectItem,
  TownSelect,
  TownSelectItem,
} from 'src/components/TownSelect/TownSelect';
import { useHost } from 'src/providers/HostProvider';
import {
  getRoomsFiltersCount,
  getRoomsSeatCountOptions,
  getRoomsState,
} from 'src/store/rooms/selectors';
import { roomsActions } from 'src/store/rooms/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { noop } from 'src/utils/noop';

import { RoomBookingDateField } from '../RoomBookingSettings/RoomBookingDateField';
import { RoomBookingTimeField } from '../RoomBookingSettings/RoomBookingTimeField';
import { RoomSearch } from '../RoomSearch/RoomSearch';

import classes from './RoomMapSettingsBottomSheet.module.scss';

const filterFloorOptions = (option: FloorSelectItem): boolean =>
  !option.value.isParking;

const Inner: FC = () => {
  const dispatch = useAppDispatch();

  const handleChangeTown = useCallback(
    (value: TownSelectItem | undefined) => {
      dispatch(roomsActions.setTown(toTown(value)));
      dispatch(roomsActions.setBuilding(undefined));
      dispatch(roomsActions.setFloor(undefined));
    },
    [dispatch],
  );

  const handleChangeBuilding = useCallback(
    (value: BuildingSelectItem | undefined) => {
      dispatch(roomsActions.setBuilding(toBuilding(value)));
      dispatch(roomsActions.setFloor(undefined));
    },
    [dispatch],
  );

  const handleChangeFloor = useCallback(
    (value: FloorSelectItem | undefined) => {
      dispatch(roomsActions.setFloor(toFloor(value)));
    },
    [dispatch],
  );

  const handleChangeWithTv = useCallback(
    (value: boolean) => {
      dispatch(roomsActions.setWithTvFilter(value));
    },
    [dispatch],
  );

  const handleChangeWithVideoCalls = useCallback(
    (value: boolean) => {
      dispatch(roomsActions.setWithVideoCallsFilter(value));
    },
    [dispatch],
  );

  const handleChangeWithFlipChart = useCallback(
    (value: boolean) => {
      dispatch(roomsActions.setWithFlipChartFilter(value));
    },
    [dispatch],
  );

  const handleChangeWithWater = useCallback(
    (value: boolean) => {
      dispatch(roomsActions.setWithWaterFilter(value));
    },
    [dispatch],
  );

  const handleChangeSeatCount = useCallback(
    (items: Option<number>[]) => {
      dispatch(roomsActions.setSeatCountFilter(items));
    },
    [dispatch],
  );

  const { town, building, floor, filters, loading } =
    useSelector(getRoomsState);
  const seatCountOptions = useSelector(getRoomsSeatCountOptions);
  const filtersCount = useSelector(getRoomsFiltersCount);

  const townSelectItem = useMemo(() => toTownSelectItem(town), [town]);
  const buildingSelectItem = useMemo(
    () => toBuildingSelectItem(building),
    [building],
  );
  const floorSelectItem = useMemo(() => toFloorSelectItem(floor), [floor]);

  return (
    <BottomSheet
      isOpen
      handleSetOpen={noop}
      swiper
      modal={false}
      snapPoints={[70, 0.5, 1]}
      style={{ minHeight: '70px' }}
      className={classes.settings}
      initialSnapPoint={0}
      header={{
        icon: Icons.Settings,
        title: (
          <BottomSheetTitle title='Параметры' filtersCount={filtersCount} />
        ) as unknown as string,
      }}
    >
      <div className={classes.container}>
        <Section title='Дата и время'>
          <RoomBookingDateField />
          <RoomBookingTimeField />
        </Section>

        <Section title='Местоположение'>
          <TownSelect town={townSelectItem} onChange={handleChangeTown} />
          <BuildingSelect
            townId={town?.townId}
            building={buildingSelectItem}
            onChange={handleChangeBuilding}
            key={town?.townId}
          />
          <FloorSelect
            buildingId={building?.buildingId}
            floor={floorSelectItem}
            onChange={handleChangeFloor}
            filterOptions={filterFloorOptions}
            key={building?.buildingId}
          />
        </Section>

        <Section title='Атрибуты комнаты'>
          <Select
            size='medium'
            label='Количество мест'
            placeholder='выберите количество мест'
            noOptionsText='нет объектов бронирования'
            loading={loading}
            options={seatCountOptions}
            values={filters.seatCount}
            onChange={handleChangeSeatCount}
            multiple={false}
            disabled={!seatCountOptions.length}
            startAdornment={<Icon iconName={Icons.User} />}
            dataTestId='roomSeatCount'
            fullWidth
          />
          <RoomAttributes
            withTv={filters.withTv}
            onChangeWithTv={handleChangeWithTv}
            withVideoCalls={filters.withVideoCalls}
            onChangeWithVideoCalls={handleChangeWithVideoCalls}
            withFlipChart={filters.withFlipChart}
            onChangeWithFlipChart={handleChangeWithFlipChart}
            withWater={filters.withWater}
            onChangeWithWater={handleChangeWithWater}
          />
        </Section>
        <Section title='Поиск по номеру'>
          <RoomSearch />
        </Section>
      </div>
    </BottomSheet>
  );
};

export const RoomMapSettingsBottomSheet: FC = () => {
  const { host } = useHost();

  return host ? createPortal(<Inner />, host) : null;
};
